import { useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useQuery } from "@tanstack/react-query";
import { Eye, X } from "lucide-react";
import { ApiError } from "../../api/client";
import { setViewAs, viewAsApi } from "../../lib/viewAs/viewAs";

// "View as role…" (Admin only, D-O115): pick a role and see Summit Operations exactly as that role sees it — the menu, the
// start page, the pages and the tour — with every change switched off, for an hour at most. The server decides who may
// (a real Admin), narrows every request and refuses every change; this dialog only asks for the role.

export function ViewAsDialog({ onClose }: { onClose: () => void }) {
  const titleId = useId();
  const closeRef = useRef<HTMLButtonElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const status = useQuery({ queryKey: ["view-as", "status"], queryFn: viewAsApi.status, staleTime: 60_000, retry: false });

  useEffect(() => {
    closeRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const start = async (role: string) => {
    setBusy(role);
    setError(null);
    try {
      setViewAs(await viewAsApi.start(role));
      // Reload so the menu, the start page and every cached page follow the viewed role from a clean start.
      window.location.assign("/");
    } catch (e) {
      setBusy(null);
      setError(e instanceof ApiError ? e.message : "Viewing as that role could not start. Try again.");
    }
  };

  const roles = status.data?.roles ?? [];

  return createPortal(
    <div className="fixed inset-0 z-[95] flex items-start justify-center bg-slate-900/40 p-4 pt-16 print:hidden" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div role="dialog" aria-modal="true" aria-labelledby={titleId} className="w-full max-w-md rounded-xl border border-slate-200 bg-white p-4 shadow-2xl" data-testid="view-as-dialog">
        <div className="flex items-start justify-between gap-2">
          <h2 id={titleId} className="flex items-center gap-1.5 text-base font-semibold text-slate-900">
            <Eye aria-hidden="true" className="h-4 w-4 text-brand-700" />
            View as role
          </h2>
          <button ref={closeRef} type="button" className="rounded p-1 text-slate-500 hover:bg-slate-100" aria-label="Close" onClick={onClose} data-testid="view-as-close">
            <X aria-hidden="true" className="h-4 w-4" />
          </button>
        </div>
        <p className="mt-1 text-sm text-slate-700">
          See Summit Operations exactly as this role sees it: the menu, the start page, the pages and the tour. Nothing can be changed while you view. It ends
          after an hour, or when you choose Back to Admin.
        </p>
        {status.isLoading && <p className="mt-3 text-sm text-slate-600">Loading the roles…</p>}
        {status.isError && <p className="mt-3 text-sm text-red-700">The roles could not be loaded. Close this and try again.</p>}
        {status.data && !status.data.canViewAs && <p className="mt-3 text-sm text-slate-700">Only an Admin can view the application as another role.</p>}
        {roles.length > 0 && (
          <ul className="mt-3 grid grid-cols-1 gap-1.5 sm:grid-cols-2" data-testid="view-as-roles">
            {roles.map((r) => (
              <li key={r.role}>
                <button
                  type="button"
                  className="btn-secondary w-full justify-start !px-3 !py-1.5 text-left text-sm disabled:opacity-60"
                  disabled={busy !== null}
                  onClick={() => void start(r.role)}
                  data-testid={`view-as-role-${r.role}`}
                >
                  {busy === r.role ? `Opening as ${r.label}…` : r.label}
                </button>
              </li>
            ))}
          </ul>
        )}
        {error && (
          <p role="alert" className="mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800" data-testid="view-as-error">
            {error}
          </p>
        )}
      </div>
    </div>,
    document.body
  );
}
